import { useState } from "react";
import { type BootstrapResponse, type Project, type Worker } from "../types";
import { api } from "../api";
import { efficiencyInputError, efficiencyKey, smvInputError } from "../efficiencyPresentation";
import { Select } from "./Select";
import { Button } from "./ui/Button";
import { Field } from "./ui/Field";
import { EmptyState, Feedback } from "./ui/Surface";
import { EfficiencyMatrix } from "./EfficiencyMatrix";
import "./EfficiencyEditor.css";

const sewingWorker = (worker: Worker) => worker.active && (worker.workGroups.length ? worker.workGroups.includes("sewing") : worker.primaryStage === "sewing");

export function EfficiencyEditor({ data, project, refresh, actorName, onProjectChange, unavailable = false, hasUnsavedChanges }: {
  data: BootstrapResponse; project: Project; refresh: () => Promise<void>; actorName: string;
  onProjectChange: (id: string) => void; unavailable?: boolean; hasUnsavedChanges?: (value: boolean) => void;
}) {
  const [edits, setEdits] = useState<Map<string, string>>(new Map());
  const [mode, setMode] = useState<"rate" | "smv">("rate");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const projects = [...data.projects].sort((a, b) => Number(a.status === "completed") - Number(b.status === "completed"));
  const workers = data.workers.filter(sewingWorker), tasks = project.subtasks.filter(task => task.stage === "sewing");
  const values = new Map(data.workerTaskEfficiencies.map(value => [efficiencyKey(value.workerId, value.subtaskId), value]));
  const tasksById = new Map(tasks.map(task => [task.id, task]));
  const workersById = new Map(workers.map(worker => [worker.id, worker]));

  const setEdit = (workerId: string, taskId: string, value: string) => {
    setEdits(current => { const next = new Map(current); next.set(efficiencyKey(workerId, taskId), value); hasUnsavedChanges?.(next.size > 0); return next; });
  };
  const changeProject = (id: string) => {
    if (id === project.id) return;
    if (edits.size && !window.confirm("저장하지 않은 효율 수정이 있습니다. 변경을 버리고 다른 프로젝트로 이동하시겠습니까?")) return;
    setEdits(new Map()); hasUnsavedChanges?.(false); setError(""); onProjectChange(id);
  };
  const changeMode = (next: "rate" | "smv") => {
    if (next === mode) return;
    if (edits.size && !window.confirm("저장하지 않은 수정이 있습니다. 변경을 버리고 표시 단위를 바꾸시겠습니까?")) return;
    setEdits(new Map()); hasUnsavedChanges?.(false); setMode(next);
  };
  const invalidCount = [...edits.values()].filter(value => mode === "smv" ? smvInputError(value) : efficiencyInputError(value)).length;
  const save = async () => {
    if (busy || !edits.size || invalidCount) return;
    setBusy(true); setError("");
    try {
      for (const [key, value] of edits) {
        const [workerId, taskId] = [key.slice(0, key.indexOf(":")), key.slice(key.indexOf(":") + 1)];
        if (!workersById.has(workerId) || !tasksById.has(taskId)) continue;
        await api.updateWorkerTaskEfficiency(mode === "smv"
          ? { workerId, subtaskId: taskId, smvMinutes: Number(value), updatedBy: actorName }
          : { workerId, subtaskId: taskId, unitsPerHour: Number(value), updatedBy: actorName });
      }
      setEdits(new Map()); hasUnsavedChanges?.(false);
      await refresh();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "작업 효율을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  };
  return <div className="efficiency-workspace">
    <div className="efficiency-controls">
      <Field label="프로젝트" className="efficiency-project"><Select value={project.id} disabled={busy} onChange={event => changeProject(event.target.value)} data-testid="planner-project">{projects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field>
      <div className="efficiency-mode" role="group" aria-label="효율 표시 단위">
        <Button size="compact" variant={mode === "rate" ? "primary" : "quiet"} disabled={busy} aria-pressed={mode === "rate"} onClick={() => changeMode("rate")}>개/시간</Button>
        <Button size="compact" variant={mode === "smv" ? "primary" : "quiet"} disabled={busy} aria-pressed={mode === "smv"} onClick={() => changeMode("smv")}>SMV · 분/개</Button>
      </div>
    </div>
    <div className="efficiency-summary">
      <p role="status">{mode === "smv" ? "SMV (1개당 작업 분)" : "시간당 생산량 (개/시간)"} · 작업자 <strong>{workers.length}</strong>명 · 단위 작업 <strong>{tasks.length}</strong>개</p>
      <div className="efficiency-legend"><span><i className="calculated" />예측</span><span><i className="manual" />직접 입력</span><span>— 미설정</span><span>{mode === "smv" ? "배정 불가는 개/시간에서 0 입력" : "0 제외"}</span></div>
      {unavailable && <span className="efficiency-connection" role="status">연결 복구 중 · 이전 효율표 표시<Button size="compact" disabled={busy} onClick={() => void refresh().catch(() => {})}>다시 연결</Button></span>}
    </div>
    {error && <Feedback tone="error">{error}</Feedback>}
    <div className="efficiency-stage">
      {!tasks.length ? <EmptyState>표시할 재봉 단위 작업이 없습니다.</EmptyState> : !workers.length ? <EmptyState>활성 재봉 작업자가 없습니다.</EmptyState> :
        <EfficiencyMatrix workers={workers} tasks={tasks} values={values} edits={edits} busy={busy} mode={mode} onEdit={setEdit} />}
    </div>
    <div className="efficiency-save-bar">
      <span role="status">{invalidCount ? mode === "smv" ? "SMV는 0.001~600분 사이여야 합니다." : "0~1,000,000 사이의 숫자로 입력해 주세요." : edits.size ? `저장 안 한 변경 ${edits.size}건` : ""}</span>
      <Button variant="primary" disabled={busy || !edits.size || !!invalidCount} busy={busy} onClick={() => void save()}>{busy ? "저장 중…" : `변경사항 저장${edits.size ? ` (${edits.size}건)` : ""}`}</Button>
    </div>
  </div>;
}
